import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import 'mantine-react-table/styles.css';

import {
    ActionIcon,
    Box,
    Container,
    Flex,
    Group,
    MultiSelect,
    Portal,
    Select,
    Text,
    Tooltip,
} from '@mantine/core';
import { ActionsColumn, IdColumn, NameColumn, StatusColumn } from './columns';
import { MultiSelectCreatable, StatColumn } from '../../../common';
import {
    useDebouncedCallback,
    useDisclosure,
    useLocalStorage,
} from '@mantine/hooks';
import { useMemo, useState } from 'react';

import ButtonColumn from '../../../common/tables/columns/ButtonColumn';
import DataStatsTimelineModal from '../../../modals/timeline/DataStatsTimelineModal copy';
import DatasetsTableToolbar from './toolbars/DatasetsToolbar';
import FiltersMultiSelect from '../../../common/filtersMultiSelect/FiltersMultiSelect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconPencil } from '@tabler/icons-react';
import JsonOptionsColumn from '../../../common/tables/columns/JsonOptionsColumn';
import JsonOptionsModal from '../../../modals/options/JsonOptionsModal';
import { MantineReactTable } from 'mantine-react-table';
import UpdateDataSetFilesModal from '../modals/UpdateDataSetFilesModal';
import { faRotateRight } from '@fortawesome/pro-solid-svg-icons';
import { getDatasets } from '../../../../http/apiClient';
import { naiveRound } from '../../../../utils/text';
import { sumObjectKeys } from '../../../../utils/statUtils';
import { usePaginatedQuery } from '../../../../http/queries/usePaginatedQuery';
import useRefreshDataSet from '../../../../http/mutations/data/useRefreshDataSet';

const DatasetsView = ({ ...props }) => {
    const [globalFilter, setGlobalFilter] = useState(null);
    const [sorting, setSorting] = useState([]);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 50,
    });

    const filterDebounce = useDebouncedCallback((query) => {
        setGlobalFilter(query);
    }, 600);

    const {
        isLoading,
        isError,
        error,
        data: paginatedData,
    } = usePaginatedQuery({
        queryFn: (queryParams) => getDatasets(queryParams),
        queryKey: ['data_sets'],
        refetchInterval: 15_0000,
        globalFilter,
        pagination,
        sorting,
    });

    const [
        timelineModalOpen,
        { open: openTimelineModal, close: closeTimelineModal },
    ] = useDisclosure();

    const [jsonModalProps, setJsonModalProps] = useState({});
    const [
        optionsModalOpen,
        { open: openOptionsModal, close: closeOptionsModal },
    ] = useDisclosure();

    const handleShowOptions = (accessorKey, jsonObj) => {
        const optionTitles = {
            emailStats: 'Email Stats',
            numberStats: 'Number Stats',
        };
        setJsonModalProps({
            header: optionTitles[accessorKey] || 'Stats',
            jsonObj: jsonObj,
        });
        openOptionsModal();
    };

    const { data: datasets, pagination: dataPagination } = paginatedData || {};
    const columns = useMemo(
        () => [
            IdColumn(),
            NameColumn(),
            StatusColumn(),
            StatColumn('Sent', (x) => x.stats?.send),
            StatColumn('Clicks', (x) =>
                sumObjectKeys(x.stats, ['uniqueClick'])
            ),
            StatColumn('Previews', (x) =>
                sumObjectKeys(x.stats, ['uniquePreview'])
            ),
            StatColumn(
                'CTR',
                (x) => {
                    const uniqueClicks = sumObjectKeys(x.stats, [
                        'uniqueClick',
                    ]);
                    return naiveRound((uniqueClicks / x.stats?.send) * 100, 2);
                },
                true
            ),
            StatColumn('Conversions', (x) => x.stats?.conversion),
            StatColumn(
                'CR',
                (x) => {
                    const uniqueClicks = sumObjectKeys(x.stats, [
                        'uniqueClick',
                    ]);
                    return naiveRound(
                        (x.stats?.conversion / uniqueClicks) * 100,
                        2
                    );
                },
                true
            ),
            JsonOptionsColumn({
                accessorKey: 'numberStats',
                header: 'Number Stats',
                onClick: handleShowOptions,
            }),
            JsonOptionsColumn({
                accessorKey: 'emailStats',
                header: 'Email Stats',
                onClick: handleShowOptions,
            }),
        ],
        [datasets]
    );

    /*const tableData = useMemo(
        () => (isLoading ? [] : campaigns),
        [campaigns, isLoading]
    );*/

    const tableData = useMemo(
        () => (isLoading ? [] : datasets),
        [datasets, isLoading]
    );

    const [columnVisibility, setColumnVisibility] = useLocalStorage({
        key: 'datasets-table-visibility', // a unique key for local storage
        defaultValue: {}, // default to all columns shown if empty
        getInitialValueInEffect: true,
    });

    const [columnSizing, setColumnSizing] = useLocalStorage({
        key: 'datasets-group-table-sizing', // a unique key for local storage
        defaultValue: {}, // default to all columns shown if empty
        getInitialValueInEffect: true,
    });

    const handleVisibilityChange = (changeFunc) => {
        const changes = changeFunc();
        setColumnVisibility((prevState) => ({
            ...prevState,
            ...changes,
        }));
    };

    const handlSizeChange = (changeFunc) => {
        const changes = changeFunc();
        setColumnSizing((prevState) => ({
            ...prevState,
            ...changes,
        }));
    };

    const handleFilterChange = (queryStr) => {
        filterDebounce(queryStr);
    };

    const [modalRow, setModalRow] = useState(null);
    const [editModalOpen, { open: openEditModal, close: closeEditModal }] =
        useDisclosure();

    const handleEdit = (rowData) => {
        setModalRow(rowData);
        openEditModal();
    };

    const handleTimeline = (rowData) => {
        setModalRow(rowData);
        openTimelineModal();
    };

    const tableProps = {
        columns: columns,
        data: tableData,
        enableRowActions: true,
        enableColumnResizing: true,
        enableColumnActions: false,
        enableSorting: false,
        enableTopToolbar: true,
        enableGlobalFilter: true,
        enableFilters: false,
        enableStickyHeader: true,
        enableDensityToggle: false,
        positionActionsColumn: 'last',
        layoutMode: 'semantic',
        mantineSearchTextInputProps: {
            placeholder: 'Search Datasets',
            miw: 200,
        },
        mantineTableContainerProps: {
            style: {
                minHeight: '80vh',
            },
        },
        positionGlobalFilter: 'left',
        renderTopToolbarCustomActions: () => (
            <DatasetsTableToolbar filterOptions={[]} />
        ),
        renderRowActions: (props) => (
            <ActionsColumn
                {...props}
                handleEdit={handleEdit}
                handleTimeline={handleTimeline}
            />
        ),

        manualPagination: true,
        manualSorting: true,
        onColumnVisibilityChange: handleVisibilityChange,
        onColumnSizingChange: handlSizeChange,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onGlobalFilterChange: handleFilterChange,
        rowCount: dataPagination?.totalItems || 0,
        pageCount: dataPagination?.totalPages || 1,

        initialState: {
            columnVisibility,
            pagination,
            isLoading,
            showGlobalFilter: true,
        },
        state: {
            isLoading,
            pagination,
            columnVisibility,
            columnSizing,
            showAlertBanner: isError,
            showProgressBars: isLoading,
            sorting,
            density: 'xs',
        },
    };

    return (
        <Container fluid>
            <MantineReactTable {...tableProps} />
            <JsonOptionsModal
                {...jsonModalProps}
                opened={optionsModalOpen}
                close={closeOptionsModal}
            />
            <UpdateDataSetFilesModal
                opened={editModalOpen}
                existing={modalRow}
                close={closeEditModal}
            />
            <DataStatsTimelineModal
                resource={'data/sets'}
                resourceId={modalRow?.id}
                title={`Send Timeline - ${modalRow?.name}`}
                opened={timelineModalOpen}
                close={closeTimelineModal}
            />
        </Container>
    );
};
export default DatasetsView;
